import React, { useMemo, useState } from "react";
import { useMoney } from "../../../hooks/useMoney";
import { UpcomingExpiryItem } from "../types";
import { TablePagination } from "../components/TablePagination";
import { ExportButton } from "../components/ExportButton";
import { exportToCsv } from "../components/exportUtils";

interface UpcomingExpiriesSectionProps {
  data: UpcomingExpiryItem[];
  /** When true, rendered inside a parent card (no outer border). */
  embedded?: boolean;
  /** First block in the group has no top divider. */
  embeddedLeading?: boolean;
}

export const UpcomingExpiriesSection: React.FC<UpcomingExpiriesSectionProps> = ({
  data,
  embedded = false,
  embeddedLeading = false,
}) => {
  const { code, number } = useMoney();
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const totalPages = Math.max(1, Math.ceil(data.length / pageSize));
  const visible = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return data.slice(startIndex, startIndex + pageSize);
  }, [page, data]);

  if (!data.length) return null;

  const handleExportTable = () => {
    exportToCsv(
      "upcoming-expiries.csv",
      ["Days Ahead", "Count", "Amount"],
      data.map((x) => [x.daysAhead, x.count, x.amount ?? 0])
    );
  };

  const inner = (
    <>
      <div className="border-b border-cardborder bg-gray-50 px-6 py-4">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Upcoming Expiries</h3>
            <p className="mt-0.5 text-xs text-gray-500">Vouchers expiring in the next N days</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <ExportButton label="Export CSV" onClick={handleExportTable} />
          </div>
        </div>
      </div>
      <div className="px-6 py-6">
        <div className="overflow-hidden border border-cardborder">
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs sm:text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Days Ahead</th>
                  <th className="px-3 py-2 text-right font-semibold">Count</th>
                  <th className="px-3 py-2 text-right font-semibold">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visible.map((x, idx) => (
                  <tr
                    key={x.daysAhead}
                    className={`${idx % 2 === 0 ? "bg-white" : "bg-gray-50"} transition-colors hover:bg-brand/5`}
                  >
                    <td className="px-3 py-2">Next {x.daysAhead} days</td>
                    <td className="px-3 py-2 text-right">{x.count}</td>
                    <td className="px-3 py-2 text-right">
                      {code} {number((x.amount ?? 0))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={page}
            totalPages={totalPages}
            totalItems={data.length}
            pageSize={pageSize}
            itemLabel="rows"
            onPageChange={setPage}
          />
        </div>
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className={embeddedLeading ? "" : "border-t border-cardborder"}>
        {inner}
      </div>
    );
  }

  return (
    <div className="overflow-hidden border border-cardborder bg-white shadow-sm">
      {inner}
    </div>
  );
};
